import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/authStore'
import type { ApiResponse } from '@/types/api'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

/**
 * 백엔드가 LocalDateTime(Z 없음)으로 반환하는 타임스탬프에 "Z"를 붙여
 * JavaScript에서 UTC로 올바르게 파싱되도록 정규화
 */
const ISO_DATETIME_WITHOUT_TZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/

function normalizeTimestamps(data: unknown): unknown {
  if (typeof data === 'string' && ISO_DATETIME_WITHOUT_TZ.test(data)) {
    return data + 'Z'
  }
  if (Array.isArray(data)) {
    return data.map(normalizeTimestamps)
  }
  if (data && typeof data === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data)) {
      result[key] = normalizeTimestamps(value)
    }
    return result
  }
  return data
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - 타임스탬프 정규화 + 토큰 갱신 및 에러 처리
apiClient.interceptors.response.use(
  (response) => {
    if (response.data) {
      response.data = normalizeTimestamps(response.data)
    }
    return response
  },
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const status = error.response?.status

    // 401 Unauthorized - 토큰 만료, refresh 시도
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshToken = useAuthStore.getState().refreshToken
      if (refreshToken) {
        try {
          const response = await axios.post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
            `${API_BASE_URL}/auth/refresh`,
            { refreshToken }
          )

          if (response.data.success && response.data.data) {
            const { accessToken, refreshToken: newRefreshToken } = response.data.data
            useAuthStore.getState().setTokens(accessToken, newRefreshToken)

            originalRequest.headers.Authorization = `Bearer ${accessToken}`
            return apiClient(originalRequest)
          }
        } catch {
          // Refresh 실패 - 로그아웃 후 로그인 페이지로
          useAuthStore.getState().logout()
          window.location.href = '/login'
          return Promise.reject(error)
        }
      } else {
        // Refresh token 없음 - 로그아웃 후 로그인 페이지로
        useAuthStore.getState().logout()
        window.location.href = '/login'
        return Promise.reject(error)
      }
    }

    // 403 Forbidden: 권한 없음 → 로그아웃하지 않고 에러만 전달
    // (비즈니스 로직의 ForbiddenException은 세션 무효가 아님)

    return Promise.reject(error)
  }
)

// Helper functions
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiResponse<unknown>
    return apiError?.error?.message || error.message || 'An error occurred'
  }
  return 'An unexpected error occurred'
}


